import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { overlayConfigFactory, DialogRef } from 'angular2-modal';
import { BSModalContext, Modal } from 'angular2-modal/plugins/bootstrap';
import { Cookie } from 'ng2-cookies';
import { TranslateService } from 'ng2-translate';
import { CustomerConfig, ModelState } from 'nm-socle-application/app/shared/models/customer-config';
import { BehaviorSubject, Observable } from 'rxjs';
import { ComponentsManagerService } from 'nm-socle-application/app/shared/services/component-manager.service';
import { TemplateInitService } from 'nm-socle-application/app/shared/services/template-init.service';
import { TemplateCurrentState } from 'nm-socle-application/app/shared/models/template-current-state';
import {ModalContainerComponentContext} from 'nm-socle-application/app/shared/components/modal-container/modal-container.component';

// Import de nm-socle-application
import { LoginService } from '../components/login/login.service';
import { ConfigService } from './config.service';
import { SecurityService } from './security.service';
import { CryptoService } from './crypto.service';
import { GeoServices } from './geo.service';
import { NotificationService } from 'nm-socle-application/app/components/notification/notification.service';

import { PasswordChangeComponent } from '../components/password-change/password-change.component';


import merge from 'lodash/merge';

export interface BootstrapStep {
    id: string;
    status: string;
    message: string;
}

@Injectable()
export class BootstrapService {
    public stepsProgress = new BehaviorSubject(null);
    public bootstrapFinished = new BehaviorSubject(false);
    public activatedRoute: ActivatedRouteSnapshot;
    private _recoveryPasswordToken: string;
    private _publicUserAuthentification: boolean = false;
    private _shareToken: string;
    public modalSmallPosition:string = "center";
    public showOverlay:boolean = true;



    /**
     * Creates an instance of BootstrapService.
     * Service d'initialisation de l'application
     *
     * @param {ConfigService} configService Service stockant toutes les valeurs de configurations applicatives
     * @param {Http} http Service Ajax
     * @param {SecurityService} SecurityService Service stockant les paramètres de sécurité
     * @param {GeoServices} geoServices Service d'interaction avec le back
     * @param {MapConfigService} mapConfigService gestion de la configuration de la carte
     *
     * @memberOf BootstrapService
     */
    constructor(
        private configService: ConfigService,
        private http: Http,
        private securityService: SecurityService,
        private _geoServices: GeoServices,
        private cryptoService: CryptoService,
        private _loginService: LoginService,
        private _modal: Modal,
        private _translateService: TranslateService,
        private _router: Router,
        private _componentsManagerService:ComponentsManagerService,
        private _templateInitService:TemplateInitService,
    ) {
        this._translateService.setDefaultLang('fr');
        this._translateService.use(this._translateService.getBrowserLang());

        this._loginService.reloggedInUser.subscribe((user) => {
            if (user)
                this.initLoginTimeout();
        });
    }

    /**
     * Initialisation de l'application
     *
     * @returns Observable<{}>
     * @memberof BootstrapService
     */
    public init() {
        if (!this.bootstrapFinished.getValue()) {

            return this.injectStaticConfig()
                .flatMap((value, index) => {
                    return this.injectRouteParamsConfig();
                }).flatMap((value, index) => {
                    return this.injectRemoteSecureConfig();
                }).flatMap((value, index) => {
                    return this.getCustomerConfig();
                }).flatMap((value, index) => {
                    this.setStep({ id: 'LOGIN', status: 'pending' });
                    return this.handleAutoLogin();
                })
                .flatMap((value, index) => {
                    if (this._recoveryPasswordToken) {
                        return this.askForPasswordChange(this._recoveryPasswordToken).flatMap((res, index) => {
                            this._recoveryPasswordToken = '';
                            return Observable.of(true);
                        });
                    } else {
                        return Observable.of(value);
                    }
                }).flatMap((value, index) => {
                    // If user parameters were obtained and contain an app_language field, use in the translate service
                    let appLanguage = this.configService.getUserOption('app_language');
                    if (appLanguage){
                        this._translateService.use(appLanguage);
                    }

                    // Si on a réussi à avoir un user on passe à la suite sinon modale de login
                    if (value) {
                        return Observable.of(true);
                    } else {
                        return this.changeState(this._templateInitService.currentState.state);
                    }
                }).flatMap((value, index) => {
                    return this.handleMapAccess();
                }).flatMap((value, index) => {
                    this.setStep({ id: 'LOGIN', status: 'success' });
                    return this.bootstrap();
                }).flatMap((value, index) => {
                    // Si expiration du mot de passe ou accès depuis un token de récupération de mot de passe , on demande son changement
                    let passwordChangeObs = Observable.of(true);
                    let loggedUser = this._loginService.loggedInUser.getValue();
                    if (!this._publicUserAuthentification && (!loggedUser.user_passw_valid || this._recoveryPasswordToken)) {
                        passwordChangeObs = this.changeState(ModelState.changePassword.name);
                    }

                    return passwordChangeObs;
                }).flatMap((value, index) => {
                    // Si Besoin de la validation de l'adresse mail
                    let emailChangeObs = Observable.of(true);
                    let needCheck: boolean = this.configService.get('needCheckEmail') == true;

                    if(needCheck) {
                        let loggedUser = this._loginService.loggedInUser.getValue();

                        if (!loggedUser.user_email_check) {
                            emailChangeObs = this.changeState(ModelState.changeMail.name);
                        }
                    }

                    return emailChangeObs;
                }).map((result) => {
                    this.bootstrapFinished.next(true);
                    this.initLoginTimeout();
                    return true;
                });

        } else {
            return Observable.of(true);
        }
    }

    public changeState(state:string): Observable<any> {
        let templateType:string = this._templateInitService.templateType;

        this._templateInitService.setCurrentState(new TemplateCurrentState({state: state}))

        let obs = Observable.fromPromise(this._openModal(templateType));

        return obs;
    }

    private _openModal(templateType: string): Promise<DialogRef<any>> {
        return this._modal.open(this._componentsManagerService.getComponentRef('ModalContainer'),
            overlayConfigFactory({templateType: templateType, component: 'TemplateStateContainer', modalSmallPosition: this.modalSmallPosition, showOverlay:this.showOverlay},
            ModalContainerComponentContext)).then((dialog: any) => { return dialog.result; })
    }

    private askForPasswordChange(token?:string) {
        return Observable.fromPromise(this._modal.open(PasswordChangeComponent, overlayConfigFactory({token:token}, BSModalContext)).then((dialog: any) => { return dialog.result; }));
    }

    public initLoginTimeout()
    {
        try { // pour les iframes externes
            if (window.top.document.URL != window.document.URL)
                return;
        }
        catch(ex) {
            return;
        }

        let loginTimeOut = this.configService.get('loginTimeOut');

        if (!loginTimeOut || loginTimeOut == -1)
            return;

        this.securityService.updateLastCall();
        this.checkLoginTimeOut(loginTimeOut);
    }

    public checkLoginTimeOut(loginTimeOut: number)
    {
        setTimeout(() => {
            let nextCall: number|boolean;
            if (nextCall = this.securityService.checkLoginTimeOut()) {
                this.checkLoginTimeOut(nextCall);
            }
            else {
                this._loginService.deleteUserSecurity();
                if (this.configService.get('loginTimeOutMode') == 'logout') {
                    let customerId = this.configService.get('customerId');

                    this._loginService.logout();

                    Cookie.set('timeout', '1', undefined, '/', window.location.hostname);
                    this._router.navigate([''], { queryParams: { customerId: customerId } }).then( () => window.location.reload());
                }
                else
                    this.changeState(ModelState.loginReconnect.name).subscribe(reconnect => {
                    });
            }
        }, loginTimeOut + 1);
    }

    /**
     * Consomme le fichier config.json de l'application
     *
     * @private
     * @returns Observable<Response>
     * @memberof BootstrapService
     */
    private injectStaticConfig() {
        this.setStep({ id: 'DOWNLOAD', status: 'pending' });
        let observable = this.http.get('./config.json')
            .map((res) => this._geoServices.extractData(res))
            .catch(this.handleBootstrapError.bind(this)).share();

        observable.subscribe(
            res => {
                for (let k in res) {
                    this.configService.put(k, res[k]);
                }
                this.setStep({ id: 'DOWNLOAD', status: 'success' });
            });

        return observable;
    }

    /**
     * Récupération des paramètres de config depuis cookies / config.json / paramètres get d'url
     *
     * @private
     * @returns Observable<any>
     * @memberof BootstrapService
     */
    private injectRouteParamsConfig() {
        this.setStep({ id: 'LOCAL_CONFIG', status: 'pending' });

        if (this.activatedRoute && this.activatedRoute.queryParams) {

            let params = this.activatedRoute.queryParams;

            // On arrive d'un token ou d'un cookie contenant toutes les informations de connexion
            if (params['token'] || Cookie.get('token')) {
                // In case of a connexion not encrypted by front, params[token] contains a password
                //   that is already hashed, so we prefer the connecxion by cookie
                let token = Cookie.get('token') || params['token'];
                let clefXOR = this.cryptoService.getKey();

                let tokenDecoded = this.cryptoService.XORDecrypt(decodeURIComponent(token), clefXOR);
                let tokenArray = tokenDecoded.split('|');

                if (tokenArray[0]) {
                    this.configService.put('customerId', tokenArray[0]);
                }
                if (tokenArray[1]) {
                    this.configService.put('login', tokenArray[1]);
                }
                if (tokenArray[2]) {
                    this.configService.put('pwd', tokenArray[2]);
                }
            }

            // On on est déjà connecté mais qu'on arrive avec un customerId différent, on enlève la connexion (changement de client)
            if (params['customerId']) {
                if ((this.configService.get('customerId') && params['customerId'] !== this.configService.get('customerId')) || !this.configService.get('customerId')) {
                    this.configService.put('customerIdDefault', params['customerId']);
                    this.configService.put('customerId', params['customerId']);
                    this.configService.put('login', null);
                    this.configService.put('pwd', null);
                }
            }

            if (params['theme']) {
                this.configService.put('mapId', parseInt(params['theme']));
            } else {
                this.configService.put('mapId', parseInt(this.configService.get('bouchon_map_id')));
            }

            // On a un token de récupération de mot de passe
            if (params['recoverLostPasswordToken']) {
                this._recoveryPasswordToken = params['recoverLostPasswordToken'];
            }

            // On a un token de partage
            if (params['share']) {
                this._shareToken = params['share'];
            }

            if ((this.activatedRoute.data && this.activatedRoute.data.publicUser) || this.configService.get('public_access') == true) {
                this._publicUserAuthentification = true;
            }

            this.setStep({ id: 'LOCAL_CONFIG', status: 'success' });
        }

        return Observable.of(this.activatedRoute);
    }

    /**
     * Récupération des informations de sécurité
     *
     * @private
     * @returns Observable<Response>
     * @memberof BootstrapService
     */
    private injectRemoteSecureConfig() {
        this.setStep({ 'id': 'SECURITY', status: 'pending' });

        let baseUri = this.configService.get('secure_uri');
        let uri = baseUri.replace('<protocol>', window.location.protocol).replace('<domainname>', window.location.hostname);
        let observable = this._geoServices.doRequest(uri, 'GET')
            .map((res) => this._geoServices.extractData(res))
            .catch(error => this.handleBootstrapError(error))
            .share();

        observable.subscribe((result: any) => {
            for (let k in result.endpoints) {
                this.configService.put(result.endpoints[k].id, result.endpoints[k]);
            }

            for (let def of result.security) {
                this.securityService.put(def.target, def);
            }

            if (result.variables) {
                for (let key of Object.keys( result.variables )) {
                    this.configService.put(key, result.variables[key]);
                }
            }

            this.securityService.setParameters(result.security);

            this.setStep({ 'id': 'SECURITY', status: 'success' });
        });
        return observable;
    }

    private handleAutoLogin(): Observable<boolean> {
        if (this._publicUserAuthentification) {
            // On récupère l'utilisateur public pour se connecter directement avec lui de manière temporaire
            return this._geoServices.doRequestUriKey('viewer_get_public_user_uri', 'GET')
                .map((res) => this._geoServices.extractData(res))
                .catch(error => this.handleBootstrapError(error))
                .flatMap((res, index) => {

                    this._loginService.setPassword('');
                    this._loginService.setConfigUser(res);
                    this._loginService.loggedInUser.next(res);

                    Cookie.set('token', this.securityService.generateNmToken(), undefined, '/', window.location.hostname);

                    return Observable.of(true);
                });
        }

        if (this._recoveryPasswordToken) {
            return this.askForPasswordChange(this._recoveryPasswordToken).flatMap((res, index) => {
                this._recoveryPasswordToken = '';
                return Observable.of(true);
                });
        }

        let loginObs: Observable<any>;

        // On retourne un observable sur le user connecté ou vide
        if (!this._loginService.loggedInUser.getValue()) {
            if (this.configService.get('customerId') && this.configService.get('pwd') && this.configService.get('login')) {
                // Connexion automatique si les informations sont déjà disponible
                loginObs = this._loginService.login().map((connectionString) => {
                    return true;
                }).catch((error) => {
                    // The provided identifiers are not correct. May happen for instance with a password found in a
                    //    deprecated Cookie. In this case we have to make sure that the login form will be displayed
                    //    without any assumptions on the password.
                    this.configService.put('pwd', null);
                    return Observable.of(undefined);
                });
            } else {
                loginObs = Observable.of(undefined);
            }
        } else {
            loginObs = Observable.of(this._loginService.loggedInUser.getValue());
        }

        return loginObs;
    }

    private handleMapAccess(): Observable<any> {

        const loggedInUser = this._loginService.loggedInUser.getValue();

        let hasAccessToMap = true;
        if (this.configService.get('mapId')) // il existe des accès sans carto (GCF)
            hasAccessToMap = loggedInUser.group.maps.find(mapElement => mapElement.modelvue_id === this.configService.get('mapId'));
        else if (loggedInUser.group.maps && loggedInUser.group.maps[0]) { // utile notemment pour les portails
            this.configService.put('mapId', loggedInUser.group.maps[0].modelvue_id);
            hasAccessToMap = true;
        }

        if (hasAccessToMap) {
            return Observable.of(true);
        } else {
            const message = this._translateService.instant('login.notifications.no-access');
            NotificationService.alert(message);
            return Observable.throw(message);
        }
    }

    /**
     * Récupération du bootstrap
     *
     * @private
     * @returns Observable<Response>
     * @memberof BootstrapService
     */
    public bootstrap() {
        this.setStep({ 'id': 'REMOTE_CONFIG', status: 'pending' });

        // don't use this._translateService.currentLang because it isn't necessarily up to date and I don't want to make the bootstrap wait with an emit
        let currentLang = this.configService.getUserOption('app_language') ? this.configService.getUserOption('app_language') : this._translateService.getBrowserLang();
        let observable = this._geoServices.doRequestUriKey('application_bootstrap_uri', 'GET', undefined, {'{lang}': currentLang}).share();

        observable
            .map((res) => this._geoServices.extractData(res))
            .catch(error => this.handleBootstrapError(error))
            .subscribe((result: any) => {
                for (let k in result.endpoints) {
                    this.configService.put(result.endpoints[k].id, result.endpoints[k]);
                }

                if (result.applicationList) {
                    this.configService.put('applicationList', result.applicationList);
                }

                if (result.variables) {
                    for (let key of Object.keys( result.variables )) {
                        this.configService.put(key, result.variables[key]);
                    }
                    this.configService.put('variables', result.variables);
                }

                if (result.translations) {
                    this._translateService.use(currentLang).subscribe(translations => {
                        this._translateService.setTranslation(merge(translations, result.translations), false);
                    });
                }

                this.setStep({ 'id': 'REMOTE_CONFIG', status: 'success' });
            });

        return observable;
    }

    /**
     * Gère les erreurs (au niveau de la splashcreen seulement)
     *
     * @private
     * @param {any} error une erreur
     * @returns {ErrorObservable.create} L'observable de la requête
     *
     * @memberof BootstrapService
     */
    public handleBootstrapError(error: any) {
        let step = this.stepsProgress.getValue();
        this.setStep({ id: step.id, status: 'failed' });
        return this._geoServices.handleError(error);
    }

    /**
     * Positionne l'étape courante et la fait passer dans l'observable
     *
     * @private
     * @param {any} step Etapes de progression
     * @memberof BootstrapService
     */
    public setStep(step: any) {
        this.stepsProgress.next(step);
    }

    public getCustomerConfig():Observable<CustomerConfig> {
        this.setStep({ id: 'STYLE', status: 'pending' });

        let customLoginPageUrl:any = this.configService.get('custom_login_page_url');

        let observable:Observable<CustomerConfig> = this._templateInitService.loadTemplate(customLoginPageUrl).map((customConf) => {
            if(customConf.homePage != undefined) {
                this.modalSmallPosition = customConf.homePage.positionSmall;
                this.showOverlay = customConf.homePage.showOverlay;
            }

            this.setStep({ id: 'STYLE', status: 'success' });

            return customConf;
        });

        return observable;
    }
}

@Injectable()
export class BootstrapResolver implements Resolve<any> {
    constructor(
        private _bootstrapService: BootstrapService,
    ) { }

    public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this._bootstrapService.activatedRoute = route;
        return this._bootstrapService.init();
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/bootstrap.service.ts